import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar } from '../components/ui/calendar';
import { Heart, TrendingUp, Sparkles } from 'lucide-react';
import Navigation from '../components/Navigation';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MOOD_ICONS = {
    'Grateful': '🙏',
    'Blessed': '✨',
    'Hopeful': '🌅',
    'Joyful': '😊',
    'Peaceful': '🕊️',
    'Seeking': '🔍',
    'Struggling': '💪',
    'Anxious': '😰',
    'Weary': '😔'
};

const MOOD_COLORS = {
    'Grateful': 'bg-amber-100 text-amber-800',
    'Blessed': 'bg-purple-100 text-purple-800',
    'Hopeful': 'bg-orange-100 text-orange-800',
    'Joyful': 'bg-yellow-100 text-yellow-800',
    'Peaceful': 'bg-blue-100 text-blue-800',
    'Seeking': 'bg-indigo-100 text-indigo-800',
    'Struggling': 'bg-rose-100 text-rose-800',
    'Anxious': 'bg-red-100 text-red-800',
    'Weary': 'bg-gray-100 text-gray-800'
};

export default function MoodHistory() {
    const [moods, setMoods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [moodStats, setMoodStats] = useState({});

    useEffect(() => {
        fetchMoods();
    }, []);

    const fetchMoods = async () => {
        try {
            const response = await axios.get(`${API_URL}/moods`);
            setMoods(response.data);
            calculateStats(response.data);
        } catch (error) {
            console.error('Failed to load moods:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (moodData) => {
        const stats = {};
        moodData.forEach(entry => {
            stats[entry.mood] = (stats[entry.mood] || 0) + 1;
        });
        setMoodStats(stats);
    };

    const getMoodForDate = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return moods.find(m => m.date === dateStr);
    };

    const getTopMood = () => {
        if (Object.keys(moodStats).length === 0) return null;
        return Object.entries(moodStats).reduce((a, b) => a[1] > b[1] ? a : b)[0];
    };

    const topMood = getTopMood();

    // Create a set of dates that have mood entries
    const moodDates = new Set(moods.map(m => m.date));

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8]" data-testid="mood-history-page">
            <Navigation />
            
            <main className="container-app py-8 md:py-12">
                {/* Header */}
                <div className="mb-10 animate-fade-in">
                    <div className="flex items-center gap-2 mb-4">
                        <Heart className="w-6 h-6 text-primary" />
                        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                            Emotional Journey
                        </span>
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-medium text-[#2D2A26] mb-2">
                        Mood History
                    </h1>
                    <p className="text-muted-foreground">
                        Track your emotional and spiritual journey over time
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Calendar Section */}
                    <div className="lg:col-span-2">
                        <div className="glass-heavy rounded-3xl p-6 md:p-8 animate-fade-in">
                            <h2 className="font-display text-xl text-[#2D2A26] mb-6">Calendar View</h2>
                            <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                className="rounded-2xl border-0"
                                modifiers={{
                                    hasMood: (date) => moodDates.has(format(date, 'yyyy-MM-dd'))
                                }}
                                modifiersStyles={{
                                    hasMood: {
                                        backgroundColor: 'rgba(212, 163, 115, 0.2)',
                                        borderRadius: '50%'
                                    }
                                }}
                                data-testid="mood-calendar"
                            />
                            
                            {/* Selected Date Mood */}
                            {selectedDate && (
                                <div className="mt-6 p-4 bg-muted/50 rounded-2xl">
                                    <p className="text-sm text-muted-foreground mb-2">
                                        {format(selectedDate, 'MMMM d, yyyy')}
                                    </p>
                                    {getMoodForDate(selectedDate) ? (
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl">
                                                {MOOD_ICONS[getMoodForDate(selectedDate).mood]}
                                            </span>
                                            <div>
                                                <p className="font-medium text-[#2D2A26]">
                                                    {getMoodForDate(selectedDate).mood}
                                                </p>
                                                {getMoodForDate(selectedDate).note && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {getMoodForDate(selectedDate).note}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-muted-foreground">No mood logged for this day</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats Sidebar */}
                    <div className="space-y-6">
                        {/* Top Mood */}
                        {topMood && (
                            <div className="verse-card animate-fade-in stagger-1" data-testid="top-mood-card">
                                <div className="flex items-center gap-2 mb-4">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                        Most Common Mood
                                    </span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <span className="text-4xl">{MOOD_ICONS[topMood]}</span>
                                    <div>
                                        <p className="font-display text-2xl text-[#2D2A26]">{topMood}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {moodStats[topMood]} times logged
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* All Mood Stats */}
                        <div className="glass rounded-2xl p-6 animate-fade-in stagger-2">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp className="w-5 h-5 text-primary" />
                                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                                    Mood Breakdown
                                </span>
                            </div>
                            
                            {Object.keys(moodStats).length > 0 ? (
                                <div className="space-y-3">
                                    {Object.entries(moodStats)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([mood, count]) => (
                                            <div key={mood} className="flex items-center gap-3">
                                                <span className="text-xl">{MOOD_ICONS[mood]}</span>
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <span className="text-sm font-medium">{mood}</span>
                                                        <span className="text-xs text-muted-foreground">{count}</span>
                                                    </div>
                                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                        <div 
                                                            className="h-full bg-primary rounded-full transition-all duration-500"
                                                            style={{ width: `${(count / moods.length) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-center py-4">
                                    No moods logged yet
                                </p>
                            )}
                        </div>

                        {/* Total Logs */}
                        <div className="journal-card animate-fade-in stagger-3">
                            <p className="text-3xl font-semibold text-[#2D2A26]">{moods.length}</p>
                            <p className="text-muted-foreground">Total mood entries</p>
                        </div>
                    </div>
                </div>

                {/* Recent Moods List */}
                <div className="mt-10">
                    <h2 className="font-display text-xl text-[#2D2A26] mb-6">Recent Entries</h2>
                    <div className="space-y-3">
                        {moods.slice(0, 10).map((entry, index) => (
                            <div 
                                key={entry.id}
                                className={`journal-card flex items-center gap-4 animate-fade-in stagger-${Math.min(index + 1, 6)}`}
                                data-testid={`mood-entry-${index}`}
                            >
                                <span className="text-2xl">{MOOD_ICONS[entry.mood]}</span>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm px-3 py-1 rounded-full ${MOOD_COLORS[entry.mood]}`}>
                                            {entry.mood}
                                        </span>
                                        <span className="text-sm text-muted-foreground">
                                            {format(new Date(entry.created_at), 'MMM d, yyyy')}
                                        </span>
                                    </div>
                                    {entry.note && (
                                        <p className="text-muted-foreground mt-1 text-sm">{entry.note}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
